/**
 * A single, typed shape for every failure that comes out of the API layer.
 *
 * The `kind` is what lets the UI decide *how* to react: an outage deserves the
 * global connectivity banner, a bad request deserves a toast next to the form
 * that caused it.
 */
export type ApiErrorKind =
  | 'unreachable' // the service could not be contacted at all
  | 'server'      // the service answered, but failed internally
  | 'auth'        // the session or the permissions are the problem
  | 'client'      // the request itself was rejected (validation, conflict, not found)
  | 'unknown';

interface ApiErrorOptions {
  kind?: ApiErrorKind;
  code?: number;
  endpoint?: string;
  cause?: unknown;
}

export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  readonly code?: number;
  readonly endpoint?: string;

  constructor(message: string, options: ApiErrorOptions = {}) {
    super(message || 'An unexpected error occurred.');
    this.name = 'ApiError';
    this.kind = options.kind ?? 'unknown';
    this.code = options.code;
    this.endpoint = options.endpoint;
    if (options.cause !== undefined) {
      (this as { cause?: unknown }).cause = options.cause;
    }
    // Restores the prototype chain, which extending a built-in loses when
    // transpiled down to the es6 target. Without it, `instanceof` lies.
    Object.setPrototypeOf(this, ApiError.prototype);
  }

  /**
   * True when the service as a whole is unavailable, as opposed to this one
   * request being wrong. Only these failures should raise the global banner.
   */
  get isOutage(): boolean {
    return this.kind === 'unreachable';
  }

  /**
   * The record asked for is not there: a mistyped or stale id in the address, rather than
   * anything wrong with the request or with the service. A page reached by an id answers
   * this with a not-found page of its own instead of a toast over an empty form.
   */
  get isNotFound(): boolean {
    return this.code === 404;
  }

  /**
   * The record is there but not for this session's roles. Distinct from isNotFound on purpose,
   * because the service distinguishes them: the audit trail answers 404 for an entity that has
   * no history and 403 for one this user may not read, and reporting the second as the first
   * sends the reader looking for a record that does exist.
   */
  get isForbidden(): boolean {
    return this.code === 403;
  }
}

/**
 * The canonical message for an outage. Shared so the UI can tell "we couldn't reach
 * the service" apart from a message the service itself sent back.
 */
export const SERVICE_UNREACHABLE_MESSAGE = 'The allocation service is unreachable.';

export const kindForStatus = (status: number): ApiErrorKind => {
  if (status === 401 || status === 403) return 'auth';
  // A gateway answering for a backend that isn't there is an outage on our side
  // of the wire, not something the user did.
  if (status === 502 || status === 503 || status === 504) return 'unreachable';
  if (status >= 500) return 'server';
  if (status >= 400) return 'client';
  return 'unknown';
};

/**
 * Normalises anything caught in a `catch` block into an ApiError, so callers can
 * rely on `.message` / `.kind` without defensive checks. Older call sites threw
 * bare object literals; those are still understood here.
 */
export const toApiError = (error: unknown): ApiError => {
  if (error instanceof ApiError) return error;

  if (error instanceof Error) {
    return new ApiError(error.message, { cause: error });
  }

  if (typeof error === 'object' && error !== null && 'message' in error) {
    const { message, code } = error as { message?: unknown; code?: unknown };
    const status = typeof code === 'number' ? code : undefined;
    return new ApiError(String(message ?? ''), {
      code: status,
      kind: status !== undefined ? kindForStatus(status) : 'unknown',
      cause: error
    });
  }

  return new ApiError(typeof error === 'string' ? error : 'An unexpected error occurred.', { cause: error });
};

/**
 * `ApiError.isNotFound` for anything caught in a `catch` block, where what was thrown is
 * typed `any` or `unknown`. Saves every detail page an `instanceof` dance to decide between
 * its not-found page and its error toast.
 */
export const isNotFound = (error: unknown): boolean => toApiError(error).isNotFound;

/** `ApiError.isForbidden` for anything caught in a `catch` block. */
export const isForbidden = (error: unknown): boolean => toApiError(error).isForbidden;
